/**
 * The poster shelf: the "catalogue" the funnel shows once the child's avatar
 * exists.
 *
 * Each poster is a painted background PLATE plus a placement for the captured
 * character PNG, which is composited over it at display time. That is the whole
 * personalisation: one render of the child, reused across every poster, so the
 * shelf costs nothing per title and works with no network after the analysis.
 *
 * The plates are fixed files in `assets/posters/`, painted once in the Speech
 * Blubs storybook style with an empty standing spot where the child goes. They
 * are never regenerated: the character changes per family, the plates do not.
 * Recipe: `scripts/generate-poster-plates.mjs` paints the scene (Gemini image
 * model, `plate-poster.jpg` from admin/assets/reference as the style
 * reference), then `scripts/add-poster-titles.mjs` letters the title across
 * the top, key-art style. Titles that carry the child's name get a generic
 * headline on the plate; the name stays in the caption under the card.
 */
import type { ReactNode } from 'react';

/** Where the child stands in a scene, in % of the poster box. */
export interface AvatarPlacement {
  /** Horizontal centre, 0–100. */
  x: number;
  /** Distance from the poster's bottom edge to the character's feet, 0–100. */
  bottom: number;
  /** Character height as a % of the poster height. */
  h: number;
  /** Mirror horizontally (so a shelf doesn't read as the same cut-out twice). */
  flip?: boolean;
  /** Degrees, for flying/leaping poses. */
  rotate?: number;
}

export interface Poster {
  id: string;
  /** Title, given the child's name (already escaped by React). */
  title: (name: string) => string;
  /** Optional big word laid over the artwork. Unused now that titles are painted into the plates. */
  overlay?: (name: string) => string;
  scene: ReactNode;
  avatar: AvatarPlacement;
  /**
   * The child stands on the side the plate's title leaves free (titles sit in
   * a top corner, see scripts/add-poster-titles.mjs), which is what lets them
   * be four fifths of the poster tall without lettering on their head.
   *
   * Costume plate id (see costumes.ts): the child is shown as their captured
   * head on a painted body dressed for the scene, instead of the rig's own
   * jumper. Posters without one fall back to the full captured figure.
   */
  costume?: string;
}

/** A painted plate, cover-fitted to the poster box. */
const plate = (id: string): ReactNode => <img src={`/posters/${id}.webp`} alt="" draggable={false} />;

// ── Topic posters ─────────────────────────────────────────────────────────────
/**
 * One poster per onboarding topic that has a plate: the "what <name> should
 * master next" shelf, filtered to the child's age band. Plates come from
 * scripts/generate-topic-plates.mjs (+ add-poster-titles.mjs), under
 * assets/posters/topics/. The child stands on the side the title leaves free,
 * dressed for the moment (costume `t-<id>`, see costumes.ts): a bib and spoon,
 * pyjamas with a torch, a backpack for the first day of school.
 */
const topicPlate = (id: string): ReactNode => <img src={`/posters/topics/${id}.webp`} alt="" draggable={false} />;
const ON_RIGHT: AvatarPlacement = { x: 70, bottom: 1, h: 90 };
const ON_LEFT: AvatarPlacement = { x: 30, bottom: 1, h: 90 };
const topic = (id: string, label: string, at: AvatarPlacement): Poster => ({ id: `topic-${id}`, title: () => label, scene: topicPlate(id), avatar: at, costume: `t-${id}` });

export const TOPIC_POSTERS: Record<string, Poster> = {
  spoon: topic('spoon', 'Eating with a spoon', ON_RIGHT),
  animals: topic('animals', 'Naming the animals', ON_RIGHT),
  potty: topic('potty', 'Potty training', ON_RIGHT),
  sharing: topic('sharing', 'Sharing toys', ON_RIGHT),
  fears: topic('fears', 'Fears and the dark', ON_RIGHT),
  haircut: topic('haircut', 'Haircuts', ON_LEFT),
  helping: topic('helping', 'Helping at home', ON_LEFT),
  'first-day': topic('first-day', 'First day of school', ON_LEFT),
  chores: topic('chores', 'Responsibility and chores', ON_LEFT),
  'big-feelings': topic('big-feelings', 'Managing big feelings', ON_LEFT),
  jobs: topic('jobs', 'Jobs and what I want to be', ON_LEFT),
  // the rest of the sixty, from scripts/topic-set.json (ids shared across age
  // bands get one poster with a neutral title)
  'sleep-night': topic('sleep-night', "Sleeping through the night", ON_RIGHT),
  'weaning': topic('weaning', "Big kid cup", ON_RIGHT),
  'teeth': topic('teeth', "Brushing teeth", ON_LEFT),
  'bath': topic('bath', "Bath time", ON_LEFT),
  'dressing': topic('dressing', "Getting dressed", ON_RIGHT),
  'first-words': topic('first-words', "First words", ON_LEFT),
  'goodbye': topic('goodbye', "Saying goodbye", ON_RIGHT),
  'winding-down': topic('winding-down', "Winding down for bed", ON_LEFT),
  'songs': topic('songs', "Songs and moving", ON_RIGHT),
  'grandparents': topic('grandparents', "Grandma and grandpa", ON_LEFT),
  'own-bed': topic('own-bed', "My own bed", ON_RIGHT),
  'tidying': topic('tidying', "Tidying up", ON_LEFT),
  'dentist': topic('dentist', "The dentist", ON_RIGHT),
  'tantrums': topic('tantrums', "Big feelings", ON_LEFT),
  'nursery': topic('nursery', "Starting nursery", ON_RIGHT),
  'new-baby': topic('new-baby', "A new baby", ON_LEFT),
  'colors': topic('colors', "Colors and shapes", ON_RIGHT),
  'calming': topic('calming', "Calm and breathing", ON_LEFT),
  'friends': topic('friends', "Making friends", ON_RIGHT),
  'waiting': topic('waiting', "Waiting my turn", ON_LEFT),
  'sorry': topic('sorry', "Saying sorry", ON_RIGHT),
  'jealous': topic('jealous', "Feeling jealous", ON_LEFT),
  'doctor': topic('doctor', "Doctor visits", ON_RIGHT),
  'road': topic('road', "Road safety", ON_LEFT),
  'letters': topic('letters', "Letters and numbers", ON_RIGHT),
  'heroes': topic('heroes', "Community heroes", ON_LEFT),
  'school-ready': topic('school-ready', "School ready", ON_RIGHT),
  'problems': topic('problems', "Solving problems", ON_LEFT),
  'friend-trouble': topic('friend-trouble', "Friendship troubles", ON_RIGHT),
  'safety': topic('safety', "Road and water safety", ON_RIGHT),
  'lost': topic('lost', "If I get lost", ON_RIGHT),
  'boundaries': topic('boundaries', "My body, my rules", ON_LEFT),
  'how-things-work': topic('how-things-work', "How things work", ON_RIGHT),
  'pet': topic('pet', "Looking after a pet", ON_LEFT),
  'birthdays': topic('birthdays', "Birthdays", ON_RIGHT),
  'left-out': topic('left-out', "Being left out", ON_LEFT),
  'habits': topic('habits', "Healthy habits", ON_RIGHT),
  'body': topic('body', "My body", ON_LEFT),
  'nature': topic('nature', "Looking after nature", ON_RIGHT),
  'help': topic('help', "Calling for help", ON_LEFT),
  'family-changes': topic('family-changes', "Family changes", ON_RIGHT),
  'travel': topic('travel', "Travel and holidays", ON_LEFT),
};

// ── Catalogue ─────────────────────────────────────────────────────────────────

/** The hero banner behind the big title. */
export const HERO_SCENE = plate('hero');
export const HERO_AVATAR: AvatarPlacement = { x: 76, bottom: 1, h: 96, rotate: -5 };
/** The billboard dresses the child as the super hero of its title. */
export const HERO_COSTUME = 'super';
/**
 * The same billboard on a phone. A narrow column cannot put copy beside a
 * standing figure without squeezing the paragraph into a ribbon, so the layout
 * stacks instead: the character sits in the upper right and the copy runs full
 * width beneath it.
 */
export const HERO_AVATAR_NARROW: AvatarPlacement = { x: 70, bottom: 36, h: 62, rotate: -4 };

/** The billboard's tagline, under "Super <name>". */
export const HERO_TAGLINE = 'Small. Mighty. Freckled. Ready to save the day!';

export const POSTERS: Poster[] = [
  {
    id: 'dinosaurs',
    title: () => 'Dinosaur Land: A Wonderful Adventure!',
    scene: plate('dinosaurs'),
    avatar: { x: 70, bottom: 1, h: 92 },
    costume: 'dinosaurs',
  },
  {
    id: 'letters',
    title: (n) => `${n}'s First Letters`,
    scene: plate('letters'),
    avatar: { x: 34, bottom: 1, h: 92 },
    costume: 'letters',
  },
  {
    id: 'space',
    title: () => 'Space Adventure',
    scene: plate('space'),
    avatar: { x: 66, bottom: 4, h: 88, rotate: -6 },
    costume: 'astronaut',
  },
  {
    id: 'underwater',
    title: () => 'Underwater Discovery: A Cartoon Adventure for All Ages',
    scene: plate('underwater'),
    // Floating rather than standing: a bigger tilt and a gap under the feet is
    // what sells "swimming" with a standing cut-out.
    avatar: { x: 40, bottom: 14, h: 78, rotate: -16 },
    costume: 'underwater',
  },
  {
    id: 'forest',
    title: () => 'Magic Forest: A Journey of Wonder',
    scene: plate('forest'),
    avatar: { x: 62, bottom: 2, h: 92 },
    costume: 'forest',
  },
  {
    id: 'chef',
    title: () => 'The Little Chef: A Sweet Story of Baking Fun',
    scene: plate('chef'),
    avatar: { x: 42, bottom: 1, h: 94 },
    costume: 'chef',
  },
  {
    id: 'super',
    title: (n) => `Super ${n}: Small. Mighty. Freckled. Ready to save the day!`,
    scene: plate('super'),
    avatar: { x: 62, bottom: 1, h: 95 },
    costume: 'super',
  },
  {
    id: 'animals',
    title: () => 'Animal Friends: A Cheerful Adventure!',
    scene: plate('animals'),
    avatar: { x: 70, bottom: 1, h: 92, flip: true },
    costume: 'animals',
  },
  {
    id: 'winter',
    title: () => 'Winter Wonderland',
    scene: plate('winter'),
    avatar: { x: 62, bottom: 2, h: 92 },
    costume: 'winter',
  },
  {
    id: 'mansion',
    title: () => 'Mystery Mansion: The Adventure of a Lifetime!',
    scene: plate('mansion'),
    avatar: { x: 30, bottom: 1, h: 92 },
    costume: 'mansion',
  },
];

/** The title the paywall previews: the "first cartoon" the funnel promises. */
export const PREVIEW_POSTER_ID = 'chef';
